<script setup lang="ts">
// An encrypted library entry's picture, decrypted on this device once it scrolls into view:
// cover art, or a photo's thumbnail (its preview, else the photo itself). Shows the slot
// (a placeholder) until then, or when there's none.
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { OpenCollection } from '../lib/collections'
import { entryImage, photoThumb, type OpenEntry } from '../lib/libraryPlay'
import { useSession } from '../stores/session'

/** A file's art or thumbnail, or a collection's uploaded cover (`art`). */
const props = defineProps<{ entry: OpenEntry | OpenCollection; use: 'art' | 'thumb' }>()
const session = useSession()
const root = ref<HTMLElement | null>(null)
const src = ref<string | null>(null)
let seen = false
let io: IntersectionObserver | null = null

async function load(): Promise<void> {
  const e = props.entry
  const url = await (props.use === 'thumb' && 'path' in e.meta ? photoThumb(session.api, e as OpenEntry) : entryImage(session.api, e))
  if (e === props.entry) src.value = url
}

onMounted(() => {
  if (typeof IntersectionObserver === 'undefined') return void load()
  io = new IntersectionObserver((seen_) => {
    if (seen || !seen_.some((x) => x.isIntersecting)) return
    seen = true
    io?.disconnect()
    void load()
  }, { rootMargin: '200px' })
  if (root.value) io.observe(root.value)
})
onBeforeUnmount(() => io?.disconnect())
watch(() => props.entry, () => seen && void load())
</script>

<template>
  <span ref="root" class="flex h-full w-full items-center justify-center">
    <img v-if="src" :src="src" alt="" class="h-full w-full object-cover" draggable="false" />
    <slot v-else />
  </span>
</template>
